/**
 * Shareable result cards (P2-WEB-07): a match's result as an image to post or send, and the words
 * that go with it. It's made in the browser on a canvas (nothing leaves the page until the player
 * shares), and it names no one: the player chooses who sees it, and their teammate didn't. The
 * players appear only as their beans (ADR 0018): seat colour and extra, no names.
 */

import { beanSvg } from "@obean/beans";
import type { DailyScore } from "@obean/game-contract";
import { dailyScoreText, dailyTier } from "@obean/game-contract";

export interface MatchResult {
  result: "win" | "loss";
  /** The game's MatchOutcome.stats (tank: ticks, shots, bricks, kills). */
  stats: Record<string, number>;
  /** The mission's name, from the game's manifest. */
  mission: string;
  /** The game's name, from its manifest: the card says "Beanguard by Obean" (ADR 0033). */
  game?: string;
  /** The game's steps per second, to turn `ticks` into time. */
  tickHz: number;
  /** The room's players at the end, in seat order: their bean's colour and extra. */
  players?: readonly { colour: number; extra: string | null }[];
  /** It was today's daily (P4-TANK-03): the card says so, so friends can try the same one. */
  daily?: boolean;
  /** The daily's number (ADR 0041), when it was one: "Today's daily #7". */
  dailyNumber?: number;
}

export interface CardLines {
  /** Who made it, top left: "Beanguard by Obean", or "Obean" without a game. */
  maker: string;
  headline: string;
  /** The game's stars for a win (stats.stars, 1-3) as "★★☆"; empty when it gives none. */
  stars: string;
  mission: string;
  /** Up to three short facts, e.g. "1:12", "14 enemy tanks". */
  facts: string[];
}

/** 75 s → "1:15". */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
}

const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

/** A game's name as shared outside Obean: "Beanguard by Obean" (ADR 0033). */
const byObean = (game: string | undefined) => (game ? `${game} by Obean` : "Obean");

/** What the card says. */
export function cardLines(r: MatchResult): CardLines {
  const facts: string[] = [];
  // A campaign run (P3-TANK-06): how far it got comes first.
  const missions = r.stats["missions"];
  if (missions !== undefined) facts.push(plural(missions, "mission cleared", "missions cleared"));
  const ticks = r.stats["ticks"];
  if (ticks !== undefined && r.tickHz > 0) facts.push(clock(ticks / r.tickHz));
  const kills = r.stats["kills"];
  if (kills !== undefined && kills > 0) facts.push(plural(kills, "enemy tank", "enemy tanks"));
  const bricks = r.stats["bricks"];
  if (bricks !== undefined && bricks > 0)
    facts.push(plural(bricks, "brick broken", "bricks broken"));
  const won = r.stats["stars"];
  const stars =
    r.result === "win" && won !== undefined && won >= 1
      ? "★".repeat(Math.min(3, Math.round(won))) + "☆".repeat(3 - Math.min(3, Math.round(won)))
      : "";
  return {
    maker: byObean(r.game),
    headline: r.result === "win" ? "Mission complete!" : "So close!",
    stars,
    mission: r.daily
      ? `Today's daily${r.dailyNumber ? ` #${String(r.dailyNumber)}` : ""}: ${r.mission}`
      : r.mission,
    facts: facts.slice(0, 3),
  };
}

/**
 * The result in a few words, for whoever is looking: a duel names its winner (`nameOf` a slot,
 * from 0), sides say whose team won, and a mission is the card's own headline. `me` is this
 * player's slot and team (from 0; solo: 0 and 0, against bots).
 */
export function headlineOf(
  outcome: { result: "win" | "loss"; stats: Record<string, number> },
  me: { slot: number; team?: number | undefined },
  nameOf: (slot: number) => string,
): string {
  const winner = outcome.stats["winner"];
  const winnerTeam = outcome.stats["winnerTeam"];
  if (winnerTeam !== undefined)
    return winnerTeam === 0
      ? "A draw!"
      : me.team !== undefined && winnerTeam === me.team + 1
        ? "Your team wins!"
        : "The other team wins";
  if (winner !== undefined)
    return winner === 0
      ? "A draw!"
      : winner === me.slot + 1
        ? "You win!"
        : `${nameOf(winner - 1)} wins`;
  return outcome.result === "win" ? "Mission complete!" : "So close!";
}

/** A daily's tier drawn in five of the game's marks, the rest blank (ADR 0041): "🔍🔍🔍⬜⬜". */
export const tierMarks = (mark: string, tier: number) =>
  mark.repeat(tier + 1) + "⬜".repeat(Math.max(0, 4 - tier));

/**
 * The daily as one line to paste anywhere (ADR 0041): the game and the day's number, its tier in
 * the game's marks with the tier's name and the score (or, for a game without tiers, the stars or
 * the result and one fact), and where to play it. Nothing that gives the day's daily away.
 */
export function dailyShareLine(
  r: Pick<MatchResult, "result" | "stats" | "mission" | "tickHz"> & {
    game: string;
    gameId: string;
    dailyNumber: number;
    score?: DailyScore | undefined;
  },
  origin: string,
): string {
  let rest: string[];
  if (r.score) {
    const tier = dailyTier(r.score, r);
    rest = [
      tierMarks(r.score.mark, tier),
      r.score.tiers[tier] ?? "",
      dailyScoreText(r.score, r.stats),
    ];
  } else {
    const stars = Math.min(3, Math.round(r.stats["stars"] ?? 0));
    const mark =
      r.result === "win" ? (stars > 0 ? "⭐".repeat(stars) + "☆".repeat(3 - stars) : "✅") : "❌";
    const fact = cardLines({ ...r, daily: false }).facts[0];
    rest = [mark, ...(fact ? [fact] : [])];
  }
  const line = [`Obean · ${r.game} daily #${String(r.dailyNumber)}`, ...rest];
  return `${line.join(" · ")}\n${origin}/solo?game=${encodeURIComponent(r.gameId)}`;
}

/** The words shared with the image: the result, and where to play. */
export function shareText(r: MatchResult, origin: string): string {
  const time =
    r.stats["ticks"] !== undefined && r.tickHz > 0 ? clock(r.stats["ticks"] / r.tickHz) : null;
  const mission = r.daily ? `today's daily, ${r.mission},` : r.mission;
  // With the game's name first ("Beanguard by Obean: we…"), "on Obean" would say it twice.
  const we = r.game ? `${byObean(r.game)}: we` : "We";
  const where = r.game ? "" : " on Obean";
  const done =
    r.result === "win"
      ? `${we} completed ${mission}${where}${time ? ` in ${time}` : ""}${
          r.stats["stars"] !== undefined && r.stats["stars"] >= 1
            ? ` with ${String(Math.min(3, Math.round(r.stats["stars"])))} ★`
            : ""
        }! 🎉`
      : `${we} nearly made it through ${mission}${where}.`;
  return `${done} Play together: ${origin}`;
}

export const CARD_WIDTH = 1200;
export const CARD_HEIGHT = 630;

/** A bean's size on the card, in pixels. */
export const CARD_BEAN = 150;

/** The beans on the card, as SVG: everyone's, smiling big on a win. At most four fit. */
export function cardBeans(r: MatchResult): string[] {
  return (r.players ?? []).slice(0, 4).map((p) =>
    beanSvg({
      colour: p.colour,
      extra: p.extra,
      mood: r.result === "win" ? "won" : "ready",
      size: CARD_BEAN,
    }),
  );
}

/**
 * Draws the card (1200 × 630, the usual link-preview shape) on a 2D context. `beans` are the
 * players' beans, drawn top right; without them, two plain tiles stand in.
 */
export function drawCard(
  ctx: CanvasRenderingContext2D,
  lines: CardLines,
  win: boolean,
  beans: readonly CanvasImageSource[] = [],
): void {
  const accent = win ? "#5eead4" : "#fbbf24";
  ctx.fillStyle = "#0b0d12";
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  if (beans.length) {
    // Side by side from the right edge, leaning in a little, as in the mark.
    const step = CARD_BEAN - 20;
    beans.forEach((bean, i) => {
      const x = 1120 - CARD_BEAN - (beans.length - 1 - i) * step;
      ctx.drawImage(bean, x, 30, CARD_BEAN, CARD_BEAN);
    });
  } else {
    // Two tiles side by side, top right: playing together. (Plain rectangles: roundRect is
    // missing in older Safari.)
    ctx.fillStyle = "#11151d";
    for (const x of [900, 1020]) ctx.fillRect(x, 60, 100, 100);
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(950, 110, 26, 0, Math.PI * 2);
    ctx.arc(1070, 110, 26, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = accent;
  ctx.fillRect(80, 520, 1040, 6);

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#94a0b8";
  ctx.font = "600 36px system-ui, sans-serif";
  // Kept clear of the beans on the right (four start at x 580).
  ctx.fillText(lines.maker, 80, 110, 360);
  if (lines.stars) {
    const after = 80 + Math.min(360, ctx.measureText(lines.maker).width) + 24;
    ctx.fillStyle = accent;
    ctx.font = "600 40px system-ui, sans-serif";
    ctx.fillText(lines.stars, after, 110);
  }
  ctx.fillStyle = accent;
  ctx.font = "800 88px system-ui, sans-serif";
  ctx.fillText(lines.headline, 80, 260, 1040);
  ctx.fillStyle = "#e8ecf3";
  ctx.font = "600 52px system-ui, sans-serif";
  ctx.fillText(lines.mission, 80, 340, 1040);
  ctx.fillStyle = "#e8ecf3";
  ctx.font = "500 40px system-ui, sans-serif";
  ctx.fillText(lines.facts.join("  ·  "), 80, 430, 1040);
  ctx.fillStyle = "#94a0b8";
  ctx.font = "500 30px system-ui, sans-serif";
  ctx.fillText("Played together on Obean", 80, 580);
}

/** An SVG as an image to draw on a canvas; null if this browser can't load it. */
async function svgImage(svg: string): Promise<HTMLImageElement | null> {
  const img = new Image(CARD_BEAN, CARD_BEAN);
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  try {
    await img.decode();
    return img;
  } catch {
    return null;
  }
}

async function render(r: MatchResult, beans: readonly CanvasImageSource[]): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  drawCard(ctx, cardLines(r), r.result === "win", beans);
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });
}

/**
 * The card as a PNG file, ready to share. Null where there's no canvas to draw on. If the beans
 * can't be drawn (an image that won't load, or a browser that won't export a canvas with SVG on
 * it), the card is made without them.
 */
export async function cardFile(r: MatchResult): Promise<File | null> {
  const images = await Promise.all(cardBeans(r).map(svgImage));
  const beans = images.filter((img): img is HTMLImageElement => img !== null);
  let blob: Blob | null;
  try {
    blob = await render(r, beans.length === images.length ? beans : []);
  } catch {
    blob = await render(r, []);
  }
  return blob ? new File([blob], "obean-result.png", { type: "image/png" }) : null;
}
